import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Badge,
  Box,
  ButtonIcon,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiErrorWarningLine,
  RiExternalLinkLine,
  RiAlertLine,
} from '@remixicon/react';
import { Progress } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import styles from './buildLogsTab.module.css';

interface StepData {
  name: string;
  status: string;
  conclusion: string | null;
  number: number;
}

interface JobData {
  id: number;
  name: string;
  status: string;
  conclusion: string | null;
  started_at: string | null;
  completed_at: string | null;
  steps: StepData[];
  logs: string;
}

interface RunData {
  id: number;
  status: string;
  conclusion: string | null;
  htmlUrl: string;
  createdAt: string;
  startedAt: string;
}

interface BuildLogsResponse {
  run: RunData;
  jobs: JobData[];
}

export const BuildLogsTab = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [data, setData] = useState<BuildLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasDataRef = useRef(false);

  const annotations = entity.metadata.annotations || {};
  const workflowRunUrl =
    annotations['infrastructure.backstage.io/workflow-url'] ||
    annotations['infrastructure.backstage.io/build-run-url'];
  const infraStatus = annotations['infrastructure.backstage.io/status'];
  const buildStatus = annotations['infrastructure.backstage.io/build-status'];

  useEffect(() => {
    if (!workflowRunUrl) {
      setLoading(false);
      return;
    }

    const match = workflowRunUrl.match(
      /github\.com\/([^/]+)\/([^/]+)\/actions\/runs\/(\d+)/,
    );
    if (!match) {
      setError('Invalid workflow run URL format');
      setLoading(false);
      return;
    }

    const [, owner, repo, runId] = match;
    const backendUrl = config.getString('backend.baseUrl');
    let cancelled = false;
    let timer: number | undefined;
    let lastLive = true; // keep retrying until we see a completed run

    const isLiveRun = (result: BuildLogsResponse) =>
      result.run.status === 'in_progress' ||
      result.run.status === 'queued' ||
      result.run.status === 'waiting' ||
      result.run.status === 'requested' ||
      result.run.status === 'pending' ||
      infraStatus === 'provisioning' ||
      buildStatus === 'building';

    const fetchLogs = async (isInitial: boolean) => {
      if (isInitial) {
        setLoading(true);
        setError(null);
      }
      try {
        const response = await fetch(
          `${backendUrl}/api/build-logs/run?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}&runId=${encodeURIComponent(runId)}`,
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch logs: ${response.status}`);
        }
        const result = (await response.json()) as BuildLogsResponse;
        if (!cancelled) {
          setData(result);
          hasDataRef.current = true;
          setError(null);
          lastLive = isLiveRun(result);
        }
        return result;
      } catch (err) {
        if (!cancelled && !hasDataRef.current) {
          // Hard error only before the first successful payload; otherwise retry quietly.
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
        return null;
      } finally {
        if (!cancelled && isInitial) {
          setLoading(false);
        }
      }
    };

    const scheduleNext = (result: BuildLogsResponse | null) => {
      if (cancelled) {
        return;
      }
      const stillLive =
        (result && isLiveRun(result)) ||
        (!result && lastLive) ||
        infraStatus === 'provisioning' ||
        buildStatus === 'building';
      if (!stillLive) {
        return;
      }
      // Near-live poll; keep ≤ ~4 GitHub calls/poll with step-log cache on the backend.
      const pollMs = result ? 10_000 : 5_000;
      timer = window.setTimeout(async () => {
        const next = await fetchLogs(false);
        scheduleNext(next);
      }, pollMs);
    };

    void (async () => {
      const first = await fetchLogs(true);
      scheduleNext(first);
    })();

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [workflowRunUrl, config, fetch, infraStatus, buildStatus]);

  const getStatusChip = (conclusion: string | null, status: string) => {
    if (status === 'in_progress') {
      return (
        <Badge size="small" className={`${styles.statusChip} ${styles.inProgressChip}`}>
          In Progress
        </Badge>
      );
    }
    switch (conclusion) {
      case 'success':
        return (
          <Badge size="small" className={`${styles.statusChip} ${styles.successChip}`}>
            ✅ Success
          </Badge>
        );
      case 'failure':
        return (
          <Badge size="small" className={`${styles.statusChip} ${styles.failureChip}`}>
            ❌ Failed
          </Badge>
        );
      case 'skipped':
        return (
          <Badge size="small" className={`${styles.statusChip} ${styles.skippedChip}`}>
            ⏭️ Skipped
          </Badge>
        );
      default:
        return (
          <Badge size="small" className={styles.statusChip}>
            {conclusion || status}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Box className={styles.emptyState}>
        <Progress />
        <Text variant="body-medium" style={{ marginTop: 16 }}>
          Loading workflow logs...
        </Text>
      </Box>
    );
  }

  if (!workflowRunUrl) {
    const msg =
      buildStatus === 'building' || infraStatus === 'provisioning'
        ? 'Provisioning is in progress. The workflow link will appear shortly after the catalog entry is created.'
        : 'No workflow run URL on this component. Re-run provisioning on a recent template version to capture the GitHub Actions run here.';
    return (
      <Box className={styles.emptyState}>
        <Text variant="title-small" as="h3">
          Provisioning workflow
        </Text>
        <Text variant="body-medium" color="secondary">
          {msg}
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.emptyState}>
        <Text variant="title-small" as="h3" color="danger">
          Error loading logs
        </Text>
        <Text variant="body-medium" color="secondary">
          {error}
        </Text>
      </Box>
    );
  }

  if (!data) return null;

  const runLive =
    data.run.status === 'in_progress' ||
    data.run.status === 'queued' ||
    data.run.status === 'waiting' ||
    data.run.status === 'requested' ||
    data.run.status === 'pending';

  return (
    <Box className={styles.root}>
      <Box className={styles.runInfo}>
        <Text variant="title-small" as="h3">
          Provisioning workflow
        </Text>
        {getStatusChip(data.run.conclusion, data.run.status)}
        {runLive && (
          <Badge size="small" className={`${styles.statusChip} ${styles.liveChip}`}>
            Live · refresh 10s
          </Badge>
        )}
        <Text variant="body-small" color="secondary">
          Run #{data.run.id} • Started{' '}
          {new Date(data.run.startedAt).toLocaleString()}
        </Text>
        <TooltipTrigger>
          <ButtonIcon
            aria-label="Open in GitHub"
            onPress={() =>
              window.open(data.run.htmlUrl, '_blank', 'noopener,noreferrer')
            }
          >
            <RiExternalLinkLine size={16} />
          </ButtonIcon>
          <Tooltip>Open in GitHub</Tooltip>
        </TooltipTrigger>
      </Box>

      {data.jobs.map(job => (
        <Box key={job.id} className={styles.jobCard}>
          <Box className={styles.jobHeader}>
            {getStatusChip(job.conclusion, job.status)}
            <Text variant="title-x-small" as="span">
              {job.name}
            </Text>
            {job.completed_at && job.started_at && (
              <span className={styles.jobMeta}>
                <Text variant="body-small" as="span">
                  {Math.round(
                    (new Date(job.completed_at).getTime() -
                      new Date(job.started_at).getTime()) /
                      1000,
                  )}
                  s
                </Text>
              </span>
            )}
          </Box>
          {job.steps?.length > 0 && (
            <Box className={styles.stepsList}>
              {job.steps.map(step => (
                <Box key={step.number} className={styles.stepRow}>
                  {getStatusChip(step.conclusion, step.status)}
                  <Text variant="body-small" as="span">
                    {step.name}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
          {job.logs ? (
            <JobLogViewer logs={job.logs} live={job.status === 'in_progress'} />
          ) : job.status === 'in_progress' || job.status === 'queued' ? (
            <Box className={`${styles.logBlock} ${styles.emptyLog}`}>
              <Text variant="body-small">
                Waiting for GitHub to flush the first log chunk… steps above
                update live.
              </Text>
            </Box>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

type LineLevel = 'normal' | 'error' | 'warning' | 'notice' | 'command' | 'debug';

interface LogLine {
  text: string;
  level: LineLevel;
  lineNo: number;
}

interface LogGroup {
  name: string;
  lines: LogLine[];
  errorCount: number;
  warningCount: number;
}

const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z\s?/;

function classifyLine(raw: string): { text: string; level: LineLevel } | null {
  const stripped = raw.trim();
  if (!stripped) return { text: '', level: 'normal' };

  if (stripped.startsWith('##[error]')) {
    return { text: stripped.replace('##[error]', ''), level: 'error' };
  }
  if (stripped.startsWith('##[warning]')) {
    return { text: stripped.replace('##[warning]', ''), level: 'warning' };
  }
  if (stripped.startsWith('##[notice]')) {
    return { text: stripped.replace('##[notice]', ''), level: 'notice' };
  }
  if (stripped.startsWith('##[command]')) {
    return { text: stripped.replace('##[command]', ''), level: 'command' };
  }
  if (stripped.startsWith('##[debug]')) {
    return { text: stripped.replace('##[debug]', ''), level: 'debug' };
  }
  if (stripped.startsWith('##[')) return null;
  return { text: raw, level: 'normal' };
}

function parseLogs(logText: string): LogGroup[] {
  const groups: LogGroup[] = [];
  let current: LogGroup | null = null;
  let lineCounter = 0;

  const ensureCurrent = (name: string) => {
    current = { name, lines: [], errorCount: 0, warningCount: 0 };
    groups.push(current);
  };

  for (const rawLine of logText.split('\n')) {
    const cleaned = rawLine.replace(TIMESTAMP_RE, '');
    const stripped = cleaned.trim();

    if (stripped.startsWith('##[group]')) {
      ensureCurrent(stripped.replace('##[group]', '').trim() || 'Group');
      continue;
    }
    if (stripped === '##[endgroup]') {
      current = null;
      continue;
    }

    const classified = classifyLine(cleaned);
    if (!classified) continue;

    if (!current) ensureCurrent('Setup');
    lineCounter += 1;
    current!.lines.push({
      text: classified.text,
      level: classified.level,
      lineNo: lineCounter,
    });
    if (classified.level === 'error') current!.errorCount += 1;
    if (classified.level === 'warning') current!.warningCount += 1;
  }

  for (const g of groups) {
    while (g.lines.length && !g.lines[0].text.trim()) g.lines.shift();
    while (g.lines.length && !g.lines[g.lines.length - 1].text.trim())
      g.lines.pop();
  }
  return groups.filter(g => g.lines.length > 0);
}

const JobLogViewer = ({ logs, live }: { logs: string; live?: boolean }) => {
  const groups = useMemo(() => parseLogs(logs), [logs]);
  const logScrollRef = useRef<HTMLDivElement | null>(null);

  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setOpenGroups(prev => {
      const next: Record<number, boolean> = { ...prev };
      groups.forEach((g, i) => {
        if (g.errorCount > 0) next[i] = true;
        // While live, keep the latest group open so new lines are visible.
        if (live && i === groups.length - 1) next[i] = true;
      });
      return next;
    });
  }, [groups, live]);

  useEffect(() => {
    if (!live || !logScrollRef.current) return;
    logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
  }, [logs, live]);

  const toggle = (i: number) => setOpenGroups(s => ({ ...s, [i]: !s[i] }));

  const lineClass = (level: LineLevel) => {
    switch (level) {
      case 'error':
        return styles.errorLine;
      case 'warning':
        return styles.warningLine;
      case 'notice':
        return styles.noticeLine;
      case 'command':
        return styles.commandLine;
      default:
        return '';
    }
  };

  const linePrefix = (level: LineLevel) => {
    switch (level) {
      case 'command':
        return '$ ';
      case 'error':
        return '✖ ';
      case 'warning':
        return '⚠ ';
      case 'notice':
        return 'ℹ ';
      default:
        return '';
    }
  };

  if (groups.length === 0) {
    return (
      <Box className={`${styles.logBlock} ${styles.emptyLog}`}>
        <Text variant="body-small">No log output captured.</Text>
      </Box>
    );
  }

  return (
    <div className={styles.logBlock} ref={logScrollRef}>
      {groups.map((group, gi) => {
        const open = !!openGroups[gi];
        const headerClass = group.errorCount > 0
          ? `${styles.groupRow} ${styles.groupRowError}`
          : group.warningCount > 0
            ? `${styles.groupRow} ${styles.groupRowWarning}`
            : styles.groupRow;
        return (
          <Box key={gi}>
            <Box className={headerClass} onClick={() => toggle(gi)}>
              {open ? (
                <RiArrowDownSLine size={16} className={styles.groupChevron} />
              ) : (
                <RiArrowRightSLine size={16} className={styles.groupChevron} />
              )}
              <span className={styles.groupName}>{group.name}</span>
              {group.errorCount > 0 && (
                <span className={`${styles.groupBadge} ${styles.errorBadge}`}>
                  <RiErrorWarningLine size={12} />
                  {group.errorCount}
                </span>
              )}
              {group.warningCount > 0 && (
                <span className={`${styles.groupBadge} ${styles.warningBadge}`}>
                  <RiAlertLine size={12} />
                  {group.warningCount}
                </span>
              )}
              <span className={styles.lineCount}>
                {group.lines.length} lines
              </span>
            </Box>
            {open && (
              <Box className={styles.groupBody}>
                {group.lines.map((line, li) => (
                  <Box
                    key={li}
                    className={`${styles.logLine} ${lineClass(line.level)}`}
                  >
                    <span className={styles.lineNumber}>{line.lineNo}</span>
                    <span className={styles.lineContent}>
                      {linePrefix(line.level)}
                      {line.text || ' '}
                    </span>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </div>
  );
};
